import React from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSiteInfo } from "../../hooks/useSiteInfo";

const MapSection = () => {
  const { t } = useLanguage();
  const { info } = useSiteInfo();

  const address = info("company_address", t("map.addressValue"));
  const phone = info("company_phone", t("map.phoneValue"));
  const email = info("company_email", t("map.emailValue"));
  const hours = info("business_hours", t("map.hoursValue"));
  const mapEmbedUrl = info("map_embed_url", "");
  const googleMapsLink = info("google_maps_link", `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`);
  const access1 = info("map_access_1", t("map.access1"));
  const access2 = info("map_access_2", t("map.access2"));

  const contactInfo = [
    {
      icon: MapPin,
      label: t("map.address"),
      value: address,
      link: googleMapsLink,
      isExternal: true,
    },
    {
      icon: Phone,
      label: t("map.phone"),
      value: phone,
      link: `tel:${phone.replace(/\s/g, "")}`,
    },
    {
      icon: Mail,
      label: t("map.email"),
      value: email,
      link: `mailto:${email}`,
    },
    {
      icon: Clock,
      label: t("map.hours"),
      value: hours,
    },
  ];

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            {t("map.title")}
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {t("map.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Contact Information */}
          <div className="space-y-6 order-2 lg:order-1">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              {t("map.contactInfoTitle")}
            </h3>
            {contactInfo.map((item, index) => (
              <div
                key={index}
                className="flex items-start space-x-4 p-6 rounded-xl bg-white border border-slate-100 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-subidha-blue rounded-lg flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-slate-500 mb-1">
                    {item.label}
                  </h4>
                  {item.link ? (
                    <a
                      href={item.link}
                      className="text-lg font-semibold text-blue-600 hover:text-blue-700 break-words"
                      target={item.isExternal ? "_blank" : undefined}
                      rel={item.isExternal ? "noopener noreferrer" : undefined}
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-lg font-semibold text-slate-900 break-words">
                      {item.value}
                    </p>
                  )}
                </div>
              </div>
            ))}

            {/* Access Information */}
            <div className="mt-8 p-6 bg-white rounded-xl border border-slate-100">
              <h4 className="text-lg font-semibold text-slate-900 mb-3">
                {t("map.accessTitle")}
              </h4>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start">
                  <span className="mr-2 text-blue-600">•</span>
                  <span>{access1}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-blue-600">•</span>
                  <span>{access2}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Map */}
          <div className="relative h-[500px] lg:h-[650px] rounded-2xl overflow-hidden shadow-2xl order-1 lg:order-2 lg:sticky lg:top-24">
            {mapEmbedUrl ? (
              <iframe
                src={mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={t("map.mapTitle")}
                className="absolute inset-0"
              ></iframe>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-100 text-slate-400 text-sm font-medium">
                {t("map.title")}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MapSection;
